/* ============================================================
   BLUE SALON — Wishlist page interactions
   Loaded only on the "wishlist" page template (see layout/theme.liquid).

   There is no server-side way to turn the bs_wishlist localStorage ids
   (assets/listing.js's toggleWishlist) into real product objects — Liquid's
   all_products is keyed by handle, not id, and there's no id-based storefront
   endpoint. So this file resolves them client-side against the public
   /products.json feed, then builds the exact same .plp-card markup as
   snippets/product-card.liquid so assets/listing.css styles it for free.

   Self-contained on purpose (matches assets/product.js / blue-salon.js): the
   small wishlist/toast/cart helpers are duplicated locally rather than
   shared, since this theme doesn't bundle JS.
   ============================================================ */
(function () {
  "use strict";

  var CARD_MOBILE_QUERY = "(max-width: 760px)";
  var CATALOG_PAGE_SIZE = 250;
  var CATALOG_MAX_PAGES = 60; // caps at 15,000 products scanned per session
  var CATALOG_BATCH_SIZE = 6; // pages fetched concurrently per round
  var CATALOG_TIMEOUT_MS = 20000; // never leave the page looking stuck

  // Every resolved product, keyed by id — populated as cards render (see
  // renderProducts). The Quick View drawer looks products up here instead
  // of re-fetching or parsing them back out of the card's own DOM.
  var productsById = {};

  function byId(id) {
    return document.getElementById(id);
  }

  function wishlistRoute(dataKey, fallbackPath) {
    var root = byId("wishlistRoot");
    if (root && root.dataset[dataKey]) return root.dataset[dataKey];
    var shopRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return (shopRoot || "/") + fallbackPath.replace(/^\//, "");
  }

  function productUrl(handle) {
    return wishlistRoute("productsBaseUrl", "products/") + encodeURIComponent(handle);
  }

  /* ── Wishlist storage (same "bs_wishlist" convention as listing.js) ── */
  function getWishlist() {
    try {
      return JSON.parse(localStorage.getItem("bs_wishlist") || "[]");
    } catch (e) {
      return [];
    }
  }

  function setWishlist(ids) {
    try {
      localStorage.setItem("bs_wishlist", JSON.stringify(ids));
    } catch (e) {}
  }

  function updateWishCount(count) {
    document.querySelectorAll("#wishCount").forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  /* ── Wishlist handle cache ("bs_wishlist_meta", id -> {handle}) ──
     bs_wishlist itself only ever holds ids (see assets/listing.js's
     toggleWishlist), and there's no id-based storefront lookup — hence the
     /products.json catalog scan below. That scan is fine for a handful of
     ids on a small catalog, but this store's catalog is large enough that
     it visibly wasn't "instant". Wherever a wish button is toggled on
     (product-card.liquid, the PDP, Quick View), it now also stashes the
     product's handle here, so THIS page can fetch it directly via
     /products/<handle>.js — one lightweight request per item, in parallel,
     instead of paging through the whole catalog. Only ids saved before
     this existed (or whose handle changed) fall back to the slow scan,
     and get backfilled here the moment that scan finds them, so it only
     ever happens once per item. */
  function getWishlistMeta() {
    try {
      return JSON.parse(localStorage.getItem("bs_wishlist_meta") || "{}");
    } catch (e) {
      return {};
    }
  }

  function setWishlistMeta(meta) {
    try {
      localStorage.setItem("bs_wishlist_meta", JSON.stringify(meta));
    } catch (e) {}
  }

  function saveWishlistHandle(id, handle) {
    if (!handle) return;
    var meta = getWishlistMeta();
    meta[id] = { handle: handle };
    setWishlistMeta(meta);
  }

  function forgetWishlistHandle(id) {
    var meta = getWishlistMeta();
    if (meta[id]) {
      delete meta[id];
      setWishlistMeta(meta);
    }
  }

  // /products/<handle>.js's shape differs from /products.json's in ways
  // that would silently corrupt buildCard's math if fed straight in:
  // prices are integer CENTS here (vs. /products.json's decimal-string
  // dollars), and images are plain URL strings (vs. {src, ...} objects).
  // Normalizing here means buildCard never needs to know which endpoint a
  // product came from.
  function normalizeProtocol(url) {
    return url && url.indexOf("//") === 0 ? "https:" + url : url;
  }

  function toDecimalString(cents) {
    return (cents / 100).toFixed(2);
  }

  function normalizeHandleProduct(raw) {
    var images = (raw.images || []).map(function (src) { return { src: normalizeProtocol(src) }; });

    var variants = (raw.variants || []).map(function (v) {
      var featured = null;
      if (v.featured_image) {
        var src = typeof v.featured_image === "string" ? v.featured_image : v.featured_image.src;
        if (src) featured = { src: normalizeProtocol(src) };
      }
      return {
        id: v.id,
        option1: v.option1,
        option2: v.option2,
        option3: v.option3,
        available: v.available,
        price: toDecimalString(v.price),
        compare_at_price: v.compare_at_price ? toDecimalString(v.compare_at_price) : null,
        featured_image: featured
      };
    });

    return {
      id: raw.id,
      title: raw.title,
      handle: raw.handle,
      vendor: raw.vendor,
      created_at: raw.created_at,
      published_at: raw.published_at,
      options: (raw.options || []).map(function (o) { return { name: o.name, position: o.position, values: o.values }; }),
      variants: variants,
      images: images,
      image: raw.featured_image ? { src: normalizeProtocol(raw.featured_image) } : (images[0] || null)
    };
  }

  function fetchProductByHandle(handle) {
    return fetch(productUrl(handle) + ".js")
      .then(function (r) {
        if (!r.ok) throw new Error("not found");
        return r.json();
      })
      .then(normalizeHandleProduct);
  }

  /* ── Toast pill (snippets/plp-toast.liquid, rendered on this page too) ── */
  var toastTimer = null;
  var WISH_ICON_REMOVED =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" ' +
    'stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">' +
    '<path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 1 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z"/></svg>';

  function showToast(message, isError, icon) {
    var toast = byId("plpToast");
    var text = byId("plpToastText");
    if (!toast || !text) return;
    text.textContent = message;
    var iconEl = byId("plpToastIcon");
    if (iconEl) iconEl.innerHTML = isError ? "" : (icon || "");
    toast.classList.toggle("is-error", !!isError);
    toast.setAttribute("role", isError ? "alert" : "status");
    toast.classList.add("is-visible");
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toast.classList.remove("is-visible");
    }, isError ? 5000 : 2500);
  }

  function showCardOos(card, message) {
    var media = card && card.querySelector(".plp-card-media");
    if (!media) { showToast(message, true); return; }
    var el = media.querySelector(".plp-card-oos");
    if (!el) {
      el = document.createElement("div");
      el.className = "plp-card-oos";
      el.setAttribute("role", "alert");
      media.appendChild(el);
    }
    el.textContent = message;
    clearTimeout(el._oosTimer);
    void el.offsetWidth;
    el.classList.add("is-visible");
    el._oosTimer = setTimeout(function () {
      el.classList.remove("is-visible");
      setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 320);
    }, 4000);
  }

  /* ── Mobile "added to your bag" card (snippets/plp-cart-toast.liquid) ──
     Injected on demand, same as listing.js's ensureCartToast — this page
     never server-renders it. */
  var cartToastTimer = null;
  var CART_TOAST_MS = 2000;

  function ensureCartToast() {
    if (byId("plpCartToast")) return;
    var el = document.createElement("div");
    el.className = "plp-cart-toast";
    el.id = "plpCartToast";
    el.setAttribute("role", "status");
    el.setAttribute("aria-live", "polite");
    el.setAttribute("aria-hidden", "true");
    el.innerHTML =
      '<div class="plp-cart-toast-media">' +
        '<img class="plp-cart-toast-img" id="plpCartToastImg" src="" alt="" width="64" height="80" loading="lazy">' +
      "</div>" +
      '<div class="plp-cart-toast-body">' +
        '<p class="plp-cart-toast-eyebrow">' +
          '<span class="plp-cart-toast-check" aria-hidden="true">' +
            '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>' +
          "</span>Added to your bag</p>" +
        '<p class="plp-cart-toast-title" id="plpCartToastTitle"></p>' +
        '<p class="plp-cart-toast-meta" id="plpCartToastMeta" hidden></p>' +
      "</div>" +
      '<span class="plp-cart-toast-progress" id="plpCartToastBar" aria-hidden="true"></span>';
    document.body.appendChild(el);
  }

  function showCartToast(item) {
    if (!item) {
      if (window.BS_showCartPreview) window.BS_showCartPreview();
      return;
    }
    ensureCartToast();
    var toast = byId("plpCartToast");
    if (!toast) {
      if (window.BS_showCartPreview) window.BS_showCartPreview();
      return;
    }
    var img = byId("plpCartToastImg");
    var title = byId("plpCartToastTitle");
    var meta = byId("plpCartToastMeta");
    var bar = byId("plpCartToastBar");
    var src = (item.featured_image && item.featured_image.url) || item.image || "";
    if (img) {
      img.removeAttribute("srcset");
      img.src = src;
      img.alt = item.product_title || item.title || "";
    }
    if (title) title.textContent = item.product_title || item.title || "";
    if (meta) {
      var variant = item.variant_title && item.variant_title !== "Default Title" ? item.variant_title : "";
      meta.textContent = variant;
      meta.hidden = !variant;
    }
    toast.setAttribute("aria-hidden", "false");
    toast.classList.add("is-visible");
    if (bar) {
      bar.style.transition = "none";
      bar.style.transform = "scaleX(1)";
      void bar.offsetWidth;
      bar.style.transition = "transform " + CART_TOAST_MS + "ms linear";
      bar.style.transform = "scaleX(0)";
    }
    clearTimeout(cartToastTimer);
    cartToastTimer = setTimeout(function () {
      toast.classList.remove("is-visible");
      toast.setAttribute("aria-hidden", "true");
    }, CART_TOAST_MS);
  }

  function updateCartCount() {
    fetch(wishlistRoute("cartUrl", "cart.js"))
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll("#cartCount").forEach(function (el) {
          el.textContent = cart.item_count;
          el.hidden = cart.item_count === 0;
        });
      })
      .catch(function () {});
  }

  // onError(message), when passed, overrides the default per-card floating
  // message — the Quick View uses it to show the message in-flow beneath
  // its own Add to bag button instead (see showQvOos).
  function addToCart(variantId, onSuccess, onError) {
    var root = byId("wishlistRoot");
    var addUrl = (root && root.dataset.cartAddUrl) || wishlistRoute("cartAddUrl", "cart/add.js");
    fetch(addUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data.status) {
          var msg = data.status === 422
            ? "You've already added this to your bag — that's all we have in stock."
            : "Sorry, we couldn't add that to your bag. Please try again.";
          if (onError) onError(msg);
          return;
        }
        var added = data.items && data.items[0];
        var headerWillSync = false;
        if (window.matchMedia(CARD_MOBILE_QUERY).matches) {
          showCartToast(added);
          if (window.BS_refreshCart) { window.BS_refreshCart(); headerWillSync = true; }
          // Brings the auto-hidden mobile topbar back so the bag count is visible.
          document.dispatchEvent(new CustomEvent("bs:cart:added"));
        } else if (window.BS_showCartPreview) {
          window.BS_showCartPreview();
          headerWillSync = true;
        }
        if (!headerWillSync) updateCartCount();
        if (onSuccess) onSuccess();
      })
      .catch(function () {
        var msg = "Sorry, we couldn't add that to your bag. Please try again.";
        if (onError) onError(msg);
      });
  }

  /* ── Money formatting: /products.json prices are decimal strings in the
     shop's currency, unlike /cart.js's cent integers — see blue-salon.js's
     formatCartMoney for the cart-drawer equivalent of this pattern. ── */
  function formatMoney(amount) {
    var n = parseFloat(amount) || 0;
    var currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || "";
    return (currency ? currency + " " : "") + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  // Shopify CDN resize trick (".../name.jpg?v=1" -> ".../name_800x.jpg?v=1"),
  // mirroring blue-salon.js's cartImageUrl for the cart drawer.
  function imageUrl(src, width) {
    if (!src) return "";
    return src.replace(/(\.[a-z0-9]+)(\?|$)/i, "_" + width + "x$1$2");
  }

  function escapeHtml(str) {
    return String(str == null ? "" : str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  /* ── Catalog fetch: /products.json is the only public storefront feed
     that returns full product+variant data, and it's handle/page based,
     not id based — so ids are resolved by scanning pages until every
     wishlist id is found or the catalog runs out. Cached in sessionStorage
     (cleared with the tab) so repeat visits within a session don't re-scan. ── */
  function fetchCatalogPage(page) {
    var cacheKey = "bs_wishlist_catalog_p" + page;
    var cached = null;
    try { cached = sessionStorage.getItem(cacheKey); } catch (e) {}
    if (cached) {
      try { return Promise.resolve(JSON.parse(cached)); } catch (e) {}
    }
    return fetch(wishlistRoute("productsJsonUrl", "products.json") + "?limit=" + CATALOG_PAGE_SIZE + "&page=" + page)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var products = (data && data.products) || [];
        try { sessionStorage.setItem(cacheKey, JSON.stringify(products)); } catch (e) {}
        return products;
      });
  }

  function resolveWishlistProducts(ids, onDone) {
    var remaining = {};
    ids.forEach(function (id) { remaining[id] = true; });
    var remainingCount = ids.length;
    var found = {};
    var settled = false;
    var hadError = false;
    var reachedEndOfCatalog = false;

    // Guards against onDone (which builds the whole card grid) running
    // twice: previously a plain .then().catch() meant an exception thrown
    // while building cards inside onDone was swallowed by the .catch below
    // and silently re-triggered a second, equally-broken render attempt.
    // Also the single place a hung/over-cap scan is forced to stop (the
    // timeout below), so the page never just sits on the skeleton forever.
    function finish() {
      if (settled) return;
      settled = true;
      clearTimeout(hardTimeout);
      // hadError tells the caller whether this is a genuine "scanned the
      // whole catalog and it's just not there" (safe to prune bs_wishlist)
      // vs. a network/parse failure, or timing out, partway through (never
      // prune on that — a transient fetch error or a big catalog taking too
      // long must not look like "the product is gone" and silently wipe
      // items the shopper actually still has saved).
      onDone(found, hadError || !reachedEndOfCatalog);
    }

    // A store with a large catalog (this one runs well past 250 products
    // per subcategory alone) made the original one-page-at-a-time scan
    // visibly slow — 20+ sequential round trips before the grid ever
    // appeared. Pages are now fetched CATALOG_BATCH_SIZE at a time instead
    // of strictly one after another, cutting wall-clock time roughly
    // proportional to the batch size.
    function scanBatch(startPage) {
      if (!remainingCount || startPage > CATALOG_MAX_PAGES) {
        finish();
        return;
      }
      var pages = [];
      for (var i = 0; i < CATALOG_BATCH_SIZE && startPage + i <= CATALOG_MAX_PAGES; i++) {
        pages.push(startPage + i);
      }

      Promise.all(
        pages.map(function (page) {
          // Two-argument .then (not .then().catch()): a per-page failure
          // resolves to null instead of rejecting the whole batch, so one
          // bad page doesn't abandon the others already in flight.
          return fetchCatalogPage(page).then(
            function (products) { return products; },
            function () { hadError = true; return null; }
          );
        })
      ).then(function (results) {
        var shortestPage = null; // first page in this batch shorter than a full page = end of catalog
        results.forEach(function (products, i) {
          if (!products) return;
          if (products.length < CATALOG_PAGE_SIZE && shortestPage === null) shortestPage = pages[i];
          products.forEach(function (p) {
            var idStr = String(p.id);
            if (remaining[idStr]) {
              found[idStr] = p;
              delete remaining[idStr];
              remainingCount--;
            }
          });
        });

        if (!remainingCount) {
          finish();
        } else if (shortestPage !== null) {
          reachedEndOfCatalog = true;
          finish();
        } else {
          scanBatch(startPage + CATALOG_BATCH_SIZE);
        }
      });
    }

    var hardTimeout = setTimeout(function () {
      hadError = true;
      finish();
    }, CATALOG_TIMEOUT_MS);

    scanBatch(1);
  }

  /* ── Card building: mirrors snippets/product-card.liquid's markup/classes
     field-for-field so listing.css needs no wishlist-specific styles. ── */
  function findOption(product, matcher) {
    for (var i = 0; i < (product.options || []).length; i++) {
      if (product.options[i].values.length > 1 && matcher(product.options[i].name.toLowerCase())) {
        return product.options[i];
      }
    }
    return null;
  }

  function optionNameMatches(name, aliases, fallback) {
    var normalized = String(name || "").toLocaleLowerCase();
    return String(aliases || fallback || "")
      .split(",")
      .some(function (alias) {
        alias = alias.trim().toLocaleLowerCase();
        return alias && normalized.indexOf(alias) !== -1;
      });
  }

  function optionValueAt(variant, position) {
    return variant["option" + position];
  }

  function variantsForValue(product, position, value) {
    return product.variants.filter(function (v) { return optionValueAt(v, position) === value; });
  }

  function colorInfoFor(product, colorOption, value) {
    var matches = variantsForValue(product, colorOption.position, value);
    var withImage = matches.filter(function (v) { return v.featured_image && v.featured_image.src; });
    return {
      value: value,
      variant: withImage[0] || matches[0],
      available: matches.some(function (v) { return v.available; })
    };
  }

  // Mirrors listing.js's markSwatchAvailability + repurposeSwatchForVariant:
  // the swatch row always lists colours in their natural order, but the
  // DEFAULT selection should be one the shopper can actually buy. Plain
  // "first colour" (product-card.liquid's forloop.first, which is all this
  // used to do) can easily be sold out while a neighbour — or a colour
  // further down the list than what's even shown — still has stock.
  function pickColorDefault(product, colorOption) {
    var maxSwatches = 3;
    var colorValues = colorOption.values;
    var shown = colorValues.slice(0, maxSwatches).map(function (v) { return colorInfoFor(product, colorOption, v); });

    var activeIdx = -1;
    for (var si = 0; si < shown.length; si++) {
      if (shown[si].available) { activeIdx = si; break; }
    }
    if (activeIdx === -1) {
      // None of the visible swatches are in stock — repurpose the first one
      // into whichever available variant is cheapest overall, same
      // tie-break as listing.js's repurposeSwatchForVariant.
      var bestAvailable = null;
      product.variants.forEach(function (v) {
        if (!v.available) return;
        if (!bestAvailable || parseFloat(v.price) < parseFloat(bestAvailable.price)) bestAvailable = v;
      });
      if (bestAvailable && shown.length) {
        var bestColor = optionValueAt(bestAvailable, colorOption.position);
        if (bestColor) {
          shown[0] = colorInfoFor(product, colorOption, bestColor);
          activeIdx = 0;
        }
      }
    }

    return {
      maxSwatches: maxSwatches,
      colorValues: colorValues,
      shown: shown,
      activeIdx: activeIdx,
      active: activeIdx !== -1 ? shown[activeIdx] : null
    };
  }

  function buildCard(product) {
    var productText = (window.BS_I18N && window.BS_I18N.product) || {};
    var colorOption = findOption(product, function (n) {
      return optionNameMatches(n, productText.option_colour_aliases, "color,colour");
    });
    var sizeOption = findOption(product, function (n) {
      return optionNameMatches(n, productText.option_size_aliases, "size");
    });
    var colorDefault = colorOption && colorOption.values.length > 1 ? pickColorDefault(product, colorOption) : null;

    var hasPickableBeyondColor = (product.options || []).some(function (o) {
      return o.values.length > 1 && (!colorOption || o.position !== colorOption.position);
    });

    var available = product.variants.some(function (v) { return v.available; });
    // Falls back to the button's own data-variant-id when there's no swatch
    // to read from (no colour option at all) — the only case where a
    // rendered "Add to bag" has just one possible variant to begin with.
    var defaultVariant = product.variants.filter(function (v) { return v.available; })[0] || product.variants[0];
    var prices = product.variants.map(function (v) { return parseFloat(v.price); });
    var comparePrices = product.variants.map(function (v) { return v.compare_at_price ? parseFloat(v.compare_at_price) : 0; });
    var priceMin = Math.min.apply(null, prices);
    var compareMin = Math.min.apply(null, comparePrices.map(function (c, i) { return c > prices[i] ? c : Infinity; }));
    var onSale = isFinite(compareMin);
    var priceVaries = prices.some(function (p) { return p !== prices[0]; });

    var createdMs = Date.parse(product.created_at || product.published_at || 0) || 0;
    var i18n = window.BS_I18N || {};
    // Within 30 days of creation, or carrying the season's New In tag or a
    // "Scat: New In …" tag (Theme settings > New badge) - same rule as the
    // theme's cards.
    var newPrefix = i18n.new_badge_tag_prefix;
    var isNew = (createdMs && (Date.now() - createdMs) / 86400000 <= 30) ||
      (i18n.new_badge_tags || []).some(function (t) { t = String(t).trim(); return t && (product.tags || []).indexOf(t) !== -1; }) ||
      !!(newPrefix && (product.tags || []).some(function (t) { return String(t).toLowerCase().indexOf(newPrefix) === 0; }));

    var badgeLabel = "";
    var badgeClass = "";
    if (!available) { badgeLabel = i18n.badge_sold_out || "Sold Out"; badgeClass = "dark"; }
    else if (onSale) { badgeLabel = (window.themeStrings && window.themeStrings.saleBadge) || "Special Offers"; badgeClass = "sale"; }
    else if (isNew) { badgeLabel = i18n.badge_new || "New"; badgeClass = "dark"; }

    var images = product.images || [];
    // The default colour selected above (not necessarily colorValues[0])
    // drives the card's main photo too — selectSwatch does the same on the
    // PLP once markSwatchAvailability re-points the active swatch, so the
    // whole tile (not just the swatch row) reflects a colour that's
    // actually in stock.
    var swatchImage = colorDefault && colorDefault.active && colorDefault.active.variant &&
      colorDefault.active.variant.featured_image && colorDefault.active.variant.featured_image.src;
    var mainImage = swatchImage || (product.image && product.image.src) || (images[0] && images[0].src) || "";
    var hoverImage = "";
    for (var i = 0; i < images.length; i++) {
      if (images[i].src !== mainImage) { hoverImage = images[i].src; break; }
    }

    var title = product.title;
    if (product.vendor && title.indexOf(product.vendor) === 0) {
      title = title.slice(product.vendor.length).replace(/^\s*-?\s*/, "");
    }

    var card = document.createElement("div");
    card.className = "plp-card";
    card.setAttribute("data-product-card", "");
    card.dataset.productId = String(product.id);

    var html = "";
    var href = productUrl(product.handle);
    html += '<a class="plp-card-media" href="' + escapeHtml(href) + '" data-card-media>';
    if (mainImage) {
      html +=
        '<img src="' + escapeHtml(imageUrl(mainImage, 800)) + '" ' +
        'srcset="' + escapeHtml(imageUrl(mainImage, 500)) + ' 500w, ' + escapeHtml(imageUrl(mainImage, 800)) + ' 800w, ' + escapeHtml(imageUrl(mainImage, 1200)) + ' 1200w" ' +
        'sizes="(min-width: 761px) 25vw, 50vw" alt="' + escapeHtml(product.title) + '" loading="lazy" data-card-img-main>';
    }
    if (hoverImage) {
      html +=
        '<img class="plp-card-hoverimg" src="' + escapeHtml(imageUrl(hoverImage, 800)) + '" ' +
        'srcset="' + escapeHtml(imageUrl(hoverImage, 500)) + ' 500w, ' + escapeHtml(imageUrl(hoverImage, 800)) + ' 800w, ' + escapeHtml(imageUrl(hoverImage, 1200)) + ' 1200w" ' +
        'sizes="(min-width: 761px) 25vw, 50vw" alt="" loading="lazy" aria-hidden="true">';
    }
    html +=
      '<button class="plp-wish is-active" type="button" data-wish-btn data-product-id="' + product.id + '" data-product-handle="' + escapeHtml(product.handle) + '" aria-label="Remove ' + escapeHtml(product.title) + ' from wishlist" aria-pressed="true">' +
      '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 1 0-7.8 7.8l9 9 8.8-8.6a5.5 5.5 0 0 0-.2-7.8z"/></svg>' +
      "</button>";

    var dotImages = images.filter(function (img) { return img.src; }).slice(0, 5);
    if (dotImages.length > 1) {
      html += '<div class="plp-dots">';
      dotImages.forEach(function (img, idx) {
        html +=
          '<button class="plp-dot' + (idx === 0 ? " is-active" : "") + '" type="button" data-dot ' +
          'data-src="' + escapeHtml(imageUrl(img.src, 800)) + '" aria-label="Show image ' + (idx + 1) + ' of ' + escapeHtml(product.title) + '"></button>';
      });
      html += "</div>";
    }

    if (badgeLabel) {
      html += '<span class="plp-tag ' + badgeClass + '">' + escapeHtml(badgeLabel) + "</span>";
    }

    var quickaddClass = "is-addtocart";
    var quickaddInner =
      '<svg class="plp-quickadd-plus" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>' + ((i18n.product || {}).add_to_bag || "Add to bag");
    if (!available) {
      quickaddClass = "is-soldout";
      quickaddInner = i18n.badge_sold_out || "Sold Out";
    } else if (hasPickableBeyondColor) {
      quickaddClass = "is-quickview";
      quickaddInner = i18n.quick_view || "Quick View";
    }
    html += '<div class="plp-quickadd">';
    if (!available) {
      html += '<button type="button" class="plp-quickadd-btn ' + quickaddClass + '" disabled>' + quickaddInner + "</button>";
    } else if (hasPickableBeyondColor) {
      html += '<button type="button" class="plp-quickadd-btn ' + quickaddClass + '" data-quick-view data-product-id="' + product.id + '">' + quickaddInner + "</button>";
    } else {
      html += '<button type="button" class="plp-quickadd-btn ' + quickaddClass + '" data-quick-add data-product-id="' + product.id + '" data-variant-id="' + defaultVariant.id + '">' + quickaddInner + "</button>";
    }
    html += "</div></a>";

    html += '<div class="plp-card-info">';
    html += '<div class="plp-card-row">';
    html += '<span class="plp-card-brand">' + escapeHtml(product.vendor) + "</span>";
    html += '<div class="plp-card-price"><span class="' + (onSale ? "now" : "") + '">' + (priceVaries ? formatMoney(priceMin) : formatMoney(prices[0])) + "</span>";
    if (onSale) html += '<span class="was">' + formatMoney(compareMin) + "</span>";
    html += "</div></div>";
    html += '<a class="plp-card-name" href="' + escapeHtml(href) + '">' + escapeHtml(title) + "</a>";

    if (colorDefault) {
      var maxSwatches = colorDefault.maxSwatches;
      var colorValues = colorDefault.colorValues;
      var shown = colorDefault.shown;
      var activeIdx = colorDefault.activeIdx;

      html += '<div class="plp-swatches" role="group" aria-label="Colour">';
      shown.forEach(function (info, idx) {
        var swatchImg = info.variant && info.variant.featured_image && info.variant.featured_image.src;
        html +=
          '<button class="plp-swatch' + (swatchImg ? " plp-swatch-img" : "") + (idx === activeIdx ? " is-active" : "") + (!info.available ? " is-soldout" : "") + '" ' +
          'type="button" data-swatch data-value="' + escapeHtml(info.value) + '" ' +
          (info.variant ? 'data-variant-id="' + info.variant.id + '" ' : "") +
          (swatchImg ? 'data-image="' + escapeHtml(imageUrl(swatchImg, 800)) + '" data-thumb="' + escapeHtml(imageUrl(swatchImg, 160)) + '" style="background-image:url(\'' + escapeHtml(imageUrl(swatchImg, 64)) + "');\" " : "") +
          'title="' + escapeHtml(info.value) + (!info.available ? " (Sold out)" : "") + '" ' +
          (!info.available ? "disabled aria-disabled=\"true\"" : "") + "></button>";
      });
      if (colorValues.length > maxSwatches) {
        // Unlike snippets/product-card.liquid's "+N" (which opens a Quick
        // View drawer this page doesn't have), this just becomes a normal
        // selectable swatch for that one extra colour.
        var nextInfo = colorInfoFor(product, colorOption, colorValues[maxSwatches]);
        html +=
          '<button type="button" class="plp-swatch-more" data-swatch data-value="' + escapeHtml(nextInfo.value) + '" ' +
          (nextInfo.variant ? 'data-variant-id="' + nextInfo.variant.id + '" ' : "") +
          (nextInfo.variant && nextInfo.variant.featured_image ? 'data-image="' + escapeHtml(imageUrl(nextInfo.variant.featured_image.src, 800)) + '" ' : "") +
          'title="' + escapeHtml(nextInfo.value) + (!nextInfo.available ? " (Sold out)" : "") + '"' +
          (!nextInfo.available ? " disabled" : "") +
          ">+" + (colorValues.length - maxSwatches) + "</button>";
      }
      html += "</div>";
    }

    if (sizeOption && sizeOption.values.length > 1) {
      html += '<div class="plp-sizes" role="group" aria-label="Size">';
      sizeOption.values.forEach(function (value) {
        var matches = variantsForValue(product, sizeOption.position, value);
        var valueAvailable = matches.some(function (v) { return v.available; });
        html +=
          '<button class="plp-size' + (!valueAvailable ? " is-soldout" : "") + '" type="button" data-size data-value="' +
          escapeHtml(value) + '"' + (!valueAvailable ? " disabled" : "") + ">" + escapeHtml(value) + "</button>";
      });
      html += "</div>";
    }
    html += "</div>";

    card.innerHTML = html;
    return card;
  }

  /* ── Mobile layout: on touch, the quick-add action moves from the image
     overlay into the brand/price row — mirrors listing.js's layoutCardInfo,
     which this page can't reuse since its DOM doesn't exist until fetch
     resolves (listing.js only wires up what's on the page at load). ── */
  function layoutCardInfo(scope) {
    var isMobile = window.matchMedia(CARD_MOBILE_QUERY).matches;
    (scope || document).querySelectorAll(".plp-card").forEach(function (card) {
      var row = card.querySelector(".plp-card-row");
      var media = card.querySelector(".plp-card-media");
      var name = card.querySelector(".plp-card-name");
      var price = card.querySelector(".plp-card-price");
      var quickadd = card.querySelector(".plp-quickadd");
      if (!row || !name || !price) return;
      if (isMobile) {
        if (price.parentElement === row) name.insertAdjacentElement("afterend", price);
        if (quickadd && quickadd.parentElement !== row) row.appendChild(quickadd);
      } else {
        if (price.parentElement !== row) row.appendChild(price);
        if (quickadd && media && quickadd.parentElement !== media) media.appendChild(quickadd);
      }
    });
  }

  /* ── Quick View drawer (snippets/quick-view-drawer.liquid, rendered on
     this page too) — mirrors assets/listing.js's Quick View so a product
     needing more than a colour pick (an unpicked Size, etc.) gets the same
     picker experience here as on the PLP, instead of just linking out to
     the PDP. Simpler than the PLP's version in one way: that one has to
     recover variant/option data by parsing JSON script tags off the
     card's own DOM (product-card.liquid's data-variants-json/
     data-options-json), because listing.js also has to support USF's
     grid, which publishes no such tags. Here every product is already a
     full JS object in productsById, so the drawer just reads it directly —
     no parsing, no USF fallback needed. PayLater/loyalty lines are
     skipped (no section settings back them on this page — same
     decorative-content scope-cut as everywhere else on this file). ── */
  var qvProductId = null;
  var qvVariants = [];
  var qvOptions = [];
  var qvSelected = {};

  var QV_HEART =
    '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">' +
    '<path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 1 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z"/></svg>';

  function qvPositions() {
    return qvOptions.map(function (o) { return o.position; });
  }
  function qvColorPos() {
    for (var i = 0; i < qvOptions.length; i++) if (qvOptions[i].is_color) return qvOptions[i].position;
    return 0;
  }
  function qvSizePos() {
    for (var i = 0; i < qvOptions.length; i++) if (qvOptions[i].is_size || /size/i.test(qvOptions[i].name)) return qvOptions[i].position;
    return 0;
  }

  // The shared resolver in assets/bs-variants.js — the same one the product
  // card, the Quick View drawer and the PDP call. Among variants matching
  // every already-picked position it prefers an available one, and among
  // those the cheapest, instead of the first in Shopify's arbitrary variant
  // order. Kept as a named local so the call sites below read unchanged.
  function findVariantByOptions(variants, selected, positions) {
    return window.BSVariants.resolve(variants, selected, positions);
  }

  function qvActiveVariant() {
    return findVariantByOptions(qvVariants, qvSelected, qvPositions());
  }

  // Per-value sold-out state, recomputed live from qvVariants + whatever's
  // picked at every OTHER position — so switching colour correctly updates
  // which sizes show as sold out instead of carrying over the previous
  // colour's state.
  function qvValueAvailable(position, value) {
    // Colour judged across every size, sizes within the colour — the same
    // rule as the PDP and Quick View, from assets/bs-variants.js. This drawer
    // used to cross out an in-stock colour whenever the picked size happened
    // not to exist in it.
    var option = (qvOptions || []).filter(function (opt) {
      return opt.position === position;
    })[0];
    return window.BSVariants.optionAvailable(
      qvVariants, qvPositions(), qvSelected, position, value, !!(option && option.is_color)
    );
  }

  // The product's full option structure (every option with more than one
  // value, not just what the card tile shows) — mirrors product-card.liquid's
  // options_json.
  function buildQvOptions(product, colorOption) {
    var out = [];
    var productText = (window.BS_I18N && window.BS_I18N.product) || {};
    (product.options || []).forEach(function (o) {
      if (o.values.length <= 1) return;
      var isColor = !!(colorOption && o.position === colorOption.position);
      var isSize = optionNameMatches(o.name, productText.option_size_aliases, "size");
      var values = o.values.map(function (val) {
        var matches = variantsForValue(product, o.position, val);
        var withImage = matches.filter(function (v) { return v.featured_image && v.featured_image.src; });
        var thumb = isColor && withImage[0] ? imageUrl(withImage[0].featured_image.src, 160) : null;
        return { name: val, available: matches.some(function (v) { return v.available; }), thumb: thumb };
      });
      out.push({ position: o.position, name: o.name, is_color: isColor, is_size: isSize, values: values });
    });
    return out;
  }

  // Mirrors product-card.liquid's variants_json — one entry per variant with
  // everything the drawer needs to price/select/add it.
  function buildQvVariants(product) {
    var positions = (product.options || []).map(function (o) { return o.position; });
    return product.variants.map(function (v) {
      var price = parseFloat(v.price);
      var compareHtml = null;
      if (v.compare_at_price && parseFloat(v.compare_at_price) > price) compareHtml = formatMoney(v.compare_at_price);
      return {
        id: v.id,
        available: v.available,
        options: positions.map(function (pos) { return optionValueAt(v, pos); }),
        title: v.title || "",
        image: v.featured_image && v.featured_image.src ? imageUrl(v.featured_image.src, 800) : null,
        price: price,
        price_html: formatMoney(v.price),
        compare_price_html: compareHtml,
        installment_html: formatMoney((price / 4).toFixed(2)),
        points: Math.round(price / 100),
        // Neither /products.json nor /products/<handle>.js expose live
        // inventory counts the way Liquid's variant.inventory_quantity
        // does — the PLP's own "Low in stock" note (qvLowStockHTML) simply
        // never fires here as a result.
        inventory: null
      };
    });
  }

  function qvLowStockHTML() {
    return "";
  }

  function qvMissingOption() {
    for (var i = 0; i < qvOptions.length; i++) {
      if (!qvSelected[qvOptions[i].position]) return qvOptions[i].position;
    }
    return 0;
  }

  function promptQvOptionSelection(pos) {
    var sections = document.querySelectorAll("#menQuickaddBody .men-quickadd-section");
    var index = qvOptions.map(function (o) { return o.position; }).indexOf(pos);
    var section = sections[index];
    var group = section && section.querySelector(".men-quickadd-chips, .men-quickadd-swatches");
    if (!group) return;
    group.style.outline = "2px solid var(--color-primary)";
    group.style.outlineOffset = "3px";
    setTimeout(function () {
      group.style.outline = "";
      group.style.outlineOffset = "";
    }, 1200);
  }

  function showQvOos(message) {
    var el = byId("menQuickaddOos");
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
  }

  function qvRender() {
    var body = byId("menQuickaddBody");
    var product = productsById[qvProductId];
    if (!body || !product) return;

    var variant = qvActiveVariant();
    var name = escapeHtml(product.title);
    var href = productUrl(product.handle);
    var primary = (variant && variant.image) || (product.image && imageUrl(product.image.src, 800)) || "";

    var gallery = '<div class="men-quickadd-gallery"><img src="' + escapeHtml(primary) + '" alt="' + name + '"></div>';

    var priceHTML = variant ? variant.price_html : "";
    if (variant && variant.compare_price_html) {
      priceHTML = '<span class="was">' + variant.compare_price_html + "</span>" +
        '<span class="now">' + priceHTML + "</span>";
    }

    var optionsHTML = qvOptions.map(function (opt) {
      var chosen = qvSelected[opt.position] || "";
      var values = opt.values.map(function (val) {
        var un = !qvValueAvailable(opt.position, val.name);
        var on = val.name === chosen;
        var attrs = ' data-qv-opt="' + opt.position + '" data-qv-value="' + escapeHtml(val.name) + '"' +
          (un ? ' disabled aria-disabled="true"' : "");
        if (opt.is_color) {
          var thumb = val.thumb || primary;
          var style = thumb ? "background-image:url('" + thumb + "')" : "background-color:" + String(val.name).replace(/\s+/g, "");
          return '<button type="button" class="men-quickadd-swatch' +
            (style.indexOf("background-image") !== -1 ? " men-quickadd-swatch-img" : "") +
            (on ? " is-active" : "") + (un ? " is-unavailable" : "") + '" style="' + escapeHtml(style) + '"' + attrs +
            ' title="' + escapeHtml(val.name) + (un ? " (Sold out)" : "") + '"></button>';
        }
        return '<button type="button" class="men-quickadd-chip' + (on ? " is-active" : "") +
          (un ? " is-unavailable" : "") + '"' + attrs + ">" + escapeHtml(val.name) + "</button>";
      }).join("");

      return '<div class="men-quickadd-section">' +
        '<div class="men-quickadd-sizehead">' +
          '<span class="men-quickadd-label">' + escapeHtml(opt.name) + ":<b>" + escapeHtml(chosen || "—") + "</b></span>" +
        "</div>" +
        '<div class="' + (opt.is_color ? "men-quickadd-swatches" : "men-quickadd-chips") + '">' + values + "</div></div>";
    }).join("");

    var panel = '<div class="men-quickadd-panel">' +
      '<div class="men-quickadd-titlewrap">' +
        '<span class="men-quickadd-brand">' + escapeHtml(product.vendor) + "</span>" +
        '<h3 class="men-quickadd-name" id="menQuickaddName">' + name + "</h3>" +
      "</div>" +
      '<div class="men-quickadd-pricewrap">' +
        '<div class="men-quickadd-price">' + priceHTML + "</div>" +
        '<hr class="bs-rule-gold men-quickadd-rule">' +
      "</div>" +
      optionsHTML + qvLowStockHTML() +
      '<p class="men-quickadd-oos" id="menQuickaddOos" role="alert" hidden></p>' +
      '<div class="men-quickadd-actions">' +
        '<button type="button" class="men-quickadd-cta" id="menQuickaddAdd"' +
          (!variant || !variant.available ? " disabled" : "") + '>' +
          (!variant || !variant.available ? ((window.BS_I18N && window.BS_I18N.badge_sold_out) || "Sold Out") :
            '<svg class="men-quickadd-plus" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>Add to bag') +
        "</button>" +
        '<button type="button" class="men-quickadd-wish is-active" id="menQuickaddWish" ' +
          'aria-label="Remove from wishlist" aria-pressed="true">' + QV_HEART + "</button>" +
      "</div>" +
      '<a class="men-quickadd-fulldetails" href="' + escapeHtml(href) + '">Full details</a>' +
    "</div>";

    body.innerHTML = gallery + panel;
  }

  function openQuickView(productId) {
    var scrim = byId("menQuickaddScrim");
    var drawer = byId("menQuickaddDrawer");
    var product = productsById[productId];
    if (!scrim || !drawer || !product) return;

    var colorOption = findOption(product, function (n) { return n.indexOf("colour") !== -1 || n.indexOf("color") !== -1; });
    qvProductId = productId;
    qvVariants = buildQvVariants(product);
    qvOptions = buildQvOptions(product, colorOption);
    qvSelected = {};

    // Carry over whatever the card already shows selected.
    var cardEl = document.querySelector('[data-product-card][data-product-id="' + productId + '"]');
    var activeSwatch = cardEl && cardEl.querySelector("[data-swatch].is-active");
    var activeSize = cardEl && cardEl.querySelector("[data-size].is-active");
    var colorPos = qvColorPos();
    var sizePos = qvSizePos();
    if (colorPos && activeSwatch) qvSelected[colorPos] = activeSwatch.dataset.value;
    if (sizePos && activeSize) qvSelected[sizePos] = activeSize.dataset.value;

    var seed = findVariantByOptions(qvVariants, qvSelected, qvPositions());
    // The card's own active colour can be entirely sold out (every size)
    // even though the product has stock under a different colour — drop
    // the card's picks and seed from the best available variant across the
    // whole product instead, same rule as buildCard's pickColorDefault.
    if (!seed || !seed.available) {
      var availableSeed = findVariantByOptions(qvVariants, {}, qvPositions());
      if (availableSeed && availableSeed.available) {
        seed = availableSeed;
        qvSelected = {};
      }
    }
    if (seed) {
      qvOptions.forEach(function (opt) {
        if (!qvSelected[opt.position]) {
          var value = (seed.options || [])[opt.position - 1];
          if (value) qvSelected[opt.position] = value;
        }
      });
    }

    qvRender();
    drawer.hidden = false;
    scrim.hidden = false;
    void drawer.offsetWidth; // commit the hidden state so both animate in
    drawer.classList.add("is-open");
    scrim.classList.add("is-open");
    drawer.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";
  }

  function closeQuickView() {
    var scrim = byId("menQuickaddScrim");
    var drawer = byId("menQuickaddDrawer");
    if (!scrim || !drawer || drawer.hidden) return;
    drawer.classList.remove("is-open");
    scrim.classList.remove("is-open");
    drawer.setAttribute("aria-hidden", "true");
    setTimeout(function () {
      drawer.hidden = true;
      scrim.hidden = true;
      document.body.style.overflow = "";
      qvProductId = null;
    }, 440);
  }

  function onQuickViewBodyClick(e) {
    if (!qvProductId) return;
    var el;

    if ((el = e.target.closest("[data-qv-opt]"))) {
      var qPos = parseInt(el.dataset.qvOpt, 10);
      var qVal = el.dataset.qvValue;

      // Two very different meanings of "crossed out", which this used to
      // treat as one and ignore alike:
      //   - no stock in ANY combination: genuinely sold out, nothing to
      //     select, so say so rather than swallow the tap;
      //   - no stock ALONGSIDE THE CURRENT PICKS: the colour is fine, it
      //     simply isn't stocked in the size showing right now.
      // The second was silently refused, which walled off real stock — a
      // colour carrying the only in-stock size could not be reached from
      // this drawer at all. Selectable now, with the re-seed below moving
      // the other positions onto something buyable, as the PDP and the PLP
      // Quick View both do.
      var reachable = window.BSVariants.valueAvailable(
        qvVariants, qvPositions(), {}, qPos, qVal
      );
      if (!reachable) {
        var soldOutOpt = qvOptions.filter(function (o) {
          return String(o.position) === String(qPos);
        })[0];
        showQvOos(
          (qVal || "") +
          (soldOutOpt ? " (" + soldOutOpt.name + ")" : "") +
          " is out of stock. Please choose another."
        );
        return;
      }

      qvSelected[qPos] = qVal;

      // Whatever is picked at the other position(s) may not combine with this
      // value into a variant worth landing on. Two distinct failures, and only
      // the first used to be handled:
      //   - no such variant exists at all (a colour that only comes in one
      //     size), which this guard was written for;
      //   - the variant exists but is SOLD OUT, which it was not. Switching
      //     colour kept the previous size, so picking a colour whose stock
      //     sits in a different size landed on Sold Out at full price with
      //     the in-stock size one click away and nothing pointing at it.
      //     Testing whether a variant EXISTS rather than whether it can be
      //     BOUGHT is what let that through.
      // Re-seed the other positions from whichever variant this value really
      // resolves to (available + cheapest). The clicked value itself is never
      // moved: the shopper asked for it, so a colour that is genuinely sold
      // out in every size still lands on it and says so.
      var current = findVariantByOptions(qvVariants, qvSelected, qvPositions());
      if (!current || !current.available) {
        var seedSelection = {};
        seedSelection[qPos] = qVal;
        var seed = findVariantByOptions(qvVariants, seedSelection, qvPositions());
        // Adopt a buyable combination; or any real one when the current
        // picks resolve to nothing at all, so the picker is never left empty.
        if (seed && (seed.available || !current)) {
          qvPositions().forEach(function (p) {
            if (p !== qPos) qvSelected[p] = (seed.options || [])[p - 1];
          });
        }
      }

      // The card tile only understands colour and size — keep it in sync so
      // the grid reflects the drawer's latest pick once it closes.
      var cardEl = document.querySelector('[data-product-card][data-product-id="' + qvProductId + '"]');
      if (cardEl) {
        var colorPos = qvColorPos();
        var sizePos = qvSizePos();
        if (colorPos && qvSelected[colorPos]) {
          var swatchBtn = null;
          cardEl.querySelectorAll("[data-swatch]").forEach(function (b) {
            if (!swatchBtn && b.dataset.value === qvSelected[colorPos]) swatchBtn = b;
          });
          if (swatchBtn) selectSwatch(swatchBtn);
        }
        if (sizePos && qvSelected[sizePos]) {
          cardEl.querySelectorAll("[data-size]").forEach(function (b) {
            b.classList.toggle("is-active", b.dataset.value === qvSelected[sizePos]);
          });
        }
      }
      qvRender();
      return;
    }

    if (e.target.closest("#menQuickaddWish")) {
      var cardEl2 = document.querySelector('[data-product-card][data-product-id="' + qvProductId + '"]');
      closeQuickView();
      if (cardEl2) removeCard(cardEl2, qvProductId);
      return;
    }

    if (e.target.closest("#menQuickaddAdd")) {
      var missing = qvMissingOption();
      if (missing) {
        promptQvOptionSelection(missing);
        return;
      }
      var variant = qvActiveVariant();
      if (!variant || !variant.available) return;
      addToCart(variant.id, closeQuickView, showQvOos);
    }
  }

  function bindQuickView() {
    var drawer = byId("menQuickaddDrawer");
    var scrim = byId("menQuickaddScrim");
    var closeBtn = byId("menQuickaddClose");
    var body = byId("menQuickaddBody");
    if (closeBtn) closeBtn.addEventListener("click", closeQuickView);
    if (scrim) scrim.addEventListener("click", closeQuickView);
    if (body) body.addEventListener("click", onQuickViewBodyClick);
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && drawer && drawer.classList.contains("is-open")) closeQuickView();
    });
  }

  /* ── Interactivity ────────────────────────────────────────── */
  function selectSwatch(btn) {
    var card = btn.closest("[data-product-card]");
    if (!card) return;
    var group = btn.closest(".plp-swatches");
    if (group) {
      group.querySelectorAll("[data-swatch]").forEach(function (b) { b.classList.remove("is-active"); });
    }
    btn.classList.add("is-active");
    var img = card.querySelector("[data-card-img-main]");
    if (img && btn.dataset.image) {
      img.src = btn.dataset.image;
      img.removeAttribute("srcset");
    }
    var quickAdd = card.querySelector("[data-quick-add]");
    if (quickAdd && btn.dataset.variantId) quickAdd.dataset.activeVariantId = btn.dataset.variantId;
  }

  function selectSize(btn) {
    if (btn.disabled) return;
    var group = btn.closest(".plp-sizes");
    if (group) {
      group.querySelectorAll("[data-size]").forEach(function (b) { b.classList.remove("is-active"); });
    }
    btn.classList.add("is-active");
  }

  function selectDot(btn) {
    var card = btn.closest("[data-product-card]");
    if (!card) return;
    var group = btn.closest(".plp-dots");
    if (group) group.querySelectorAll("[data-dot]").forEach(function (b) { b.classList.remove("is-active"); });
    btn.classList.add("is-active");
    var img = card.querySelector("[data-card-img-main]");
    if (img && btn.dataset.src) {
      img.src = btn.dataset.src;
      img.removeAttribute("srcset");
    }
  }

  function removeCard(card, id) {
    var ids = getWishlist().filter(function (existing) { return existing !== id; });
    setWishlist(ids);
    forgetWishlistHandle(id);
    updateWishCount(ids.length);
    updatePageCount(ids.length);
    showToast("Removed from your wishlist", false, WISH_ICON_REMOVED);
    card.classList.add("is-removing");
    setTimeout(function () {
      if (card.parentNode) card.parentNode.removeChild(card);
      if (!ids.length) showEmptyState();
    }, 260);
  }

  function quickAddDirect(card) {
    var btn = card.querySelector("[data-quick-add]");
    if (!btn) return;
    var activeSwatch = card.querySelector("[data-swatch].is-active");
    var variantId = (activeSwatch && activeSwatch.dataset.variantId) || btn.dataset.activeVariantId || btn.dataset.variantId;
    if (!variantId) return;
    addToCart(variantId, null, function (msg) { showCardOos(card, msg); });
  }

  function onGridClick(e) {
    var target = e.target;
    var el;
    if ((el = target.closest("[data-wish-btn]"))) {
      e.preventDefault();
      var card = el.closest("[data-product-card]");
      if (card) removeCard(card, card.dataset.productId);
      return;
    }
    if ((el = target.closest("[data-swatch]")) && !el.disabled) { selectSwatch(el); return; }
    if ((el = target.closest("[data-size]")) && !el.disabled) { selectSize(el); return; }
    if ((el = target.closest("[data-dot]"))) { e.preventDefault(); selectDot(el); return; }
    if ((el = target.closest("[data-quick-view]"))) {
      e.preventDefault();
      openQuickView(el.dataset.productId);
      return;
    }
    if ((el = target.closest("[data-quick-add]")) && !el.disabled) {
      e.preventDefault();
      var quickAddCard = el.closest("[data-product-card]");
      if (quickAddCard) quickAddDirect(quickAddCard);
    }
  }

  /* ── Page chrome ──────────────────────────────────────────── */
  function updatePageCount(count) {
    var el = byId("wishlistPageCount");
    if (!el) return;
    var translations = window.BS_I18N || {};
    var template = count === 1 ? translations.category_items_one : translations.category_items_many;
    el.textContent = template
      ? template.replace("__COUNT__", count)
      : count + (count === 1 ? " item" : " items");
    el.hidden = count === 0;
  }

  function showEmptyState() {
    var empty = byId("wishlistEmpty");
    var grid = byId("wishlistGrid");
    var skeleton = byId("wishlistSkeleton");
    var clearBtn = byId("wishlistClearAll");
    if (skeleton) skeleton.hidden = true;
    if (grid) grid.hidden = true;
    if (empty) empty.hidden = false;
    if (clearBtn) clearBtn.hidden = true;
  }

  function clearAll() {
    if (!window.confirm("Remove everything from your wishlist?")) return;
    setWishlist([]);
    setWishlistMeta({});
    updateWishCount(0);
    updatePageCount(0);
    var grid = byId("wishlistGrid");
    if (grid) grid.innerHTML = "";
    showEmptyState();
  }

  function init() {
    var root = byId("wishlistRoot");
    if (!root) return;
    var grid = byId("wishlistGrid");
    var skeleton = byId("wishlistSkeleton");
    var clearBtn = byId("wishlistClearAll");
    if (!grid) return;

    var ids = getWishlist();
    updateWishCount(ids.length);

    if (!ids.length) {
      showEmptyState();
      return;
    }

    grid.addEventListener("click", onGridClick);
    bindQuickView();
    if (clearBtn) {
      clearBtn.hidden = false;
      clearBtn.addEventListener("click", clearAll);
    }

    // Newest-saved first — toggleWishlist appends to the end of the array.
    var orderedIds = ids.slice().reverse();
    var meta = getWishlistMeta();
    var renderedIds = {}; // guards against double-rendering an id across the two phases below

    var fastIds = [];
    var legacyIds = [];
    orderedIds.forEach(function (id) {
      if (meta[id] && meta[id].handle) fastIds.push(id); else legacyIds.push(id);
    });

    // One malformed/unexpected product shape must not blank the whole grid —
    // build each card in isolation, skip (and log) any that throw.
    function renderProducts(idList, productMap) {
      var frag = document.createDocumentFragment();
      var builtCount = 0;
      idList.forEach(function (id) {
        if (renderedIds[id]) return;
        var product = productMap[id];
        if (!product) return;
        try {
          frag.appendChild(buildCard(product));
          renderedIds[id] = true;
          productsById[id] = product;
          builtCount++;
        } catch (err) {
          if (window.console && console.error) console.error("Wishlist: failed to render product " + id, err);
        }
      });
      if (builtCount) {
        // Only hide the skeleton once there's actually something to replace
        // it with — if a wishlist is entirely legacy ids (no cached handle
        // yet), the fast phase below renders nothing and this only fires
        // once the slower catalog scan finishes, so the shimmer correctly
        // stays up for the whole wait instead of leaving a blank gap.
        if (skeleton) skeleton.hidden = true;
        grid.appendChild(frag);
        grid.hidden = false;
        updatePageCount(Object.keys(renderedIds).length);
        layoutCardInfo(grid);
      }
      return builtCount;
    }

    function showLoadFailure() {
      showEmptyState();
      var emptyMsg = byId("wishlistEmpty");
      if (emptyMsg) {
        var p = emptyMsg.querySelector("p");
        if (p) p.textContent = "We couldn't load your saved items right now. Please try again in a moment.";
      }
    }

    // Phase 1 — fast path: anything whose handle is already known fetches
    // directly via /products/<handle>.js, in parallel. This is the case for
    // everything wishlisted going forward, and is near-instant regardless
    // of how large the store's catalog is.
    var fastWork = fastIds.length
      ? Promise.all(fastIds.map(function (id) {
          return fetchProductByHandle(meta[id].handle).then(
            function (product) { return { id: id, product: product }; },
            // The saved handle no longer resolves (product renamed/deleted
            // since it was wishlisted) — treat it like a legacy id below.
            function () { return { id: id, product: null }; }
          );
        }))
      : Promise.resolve([]);

    fastWork.then(function (fastResults) {
      var fastMap = {};
      var staleFastIds = [];
      fastResults.forEach(function (r) {
        if (r.product) fastMap[r.id] = r.product; else staleFastIds.push(r.id);
      });
      renderProducts(fastIds, fastMap);

      var toRescan = legacyIds.concat(staleFastIds);
      if (!toRescan.length) {
        if (!Object.keys(renderedIds).length) {
          // Every fast id's handle failed to resolve and there's nothing
          // left to fall back on — treat like "scanned and found nothing".
          setWishlist([]);
          updateWishCount(0);
          showEmptyState();
        }
        return;
      }

      // Phase 2 — anything without a cached handle (saved before this
      // existed, or whose handle went stale) falls back to the slower
      // catalog scan, scoped to just those ids so it never blocks the fast
      // results above. Backfills the handle cache on success, so the same
      // items are instant on the next visit.
      resolveWishlistProducts(toRescan, function (found, hadError) {
        var scannedMap = {};
        toRescan.forEach(function (id) { if (found[id]) scannedMap[id] = found[id]; });
        var scannedCount = renderProducts(toRescan, scannedMap);

        if (!hadError) {
          var newMeta = getWishlistMeta();
          var metaChanged = false;
          toRescan.forEach(function (id) {
            if (found[id] && found[id].handle) { newMeta[id] = { handle: found[id].handle }; metaChanged = true; }
          });
          if (metaChanged) setWishlistMeta(newMeta);

          // Scanned the whole catalog cleanly and these specific ids still
          // never turned up — they're genuinely gone. Prune only them.
          var stillMissing = toRescan.filter(function (id) { return !found[id]; });
          if (stillMissing.length) {
            var currentIds = getWishlist().filter(function (id) { return stillMissing.indexOf(id) === -1; });
            setWishlist(currentIds);
            updateWishCount(currentIds.length);
          }
        }

        if (!Object.keys(renderedIds).length) {
          if (hadError) showLoadFailure();
          else { setWishlist([]); updateWishCount(0); showEmptyState(); }
        } else {
          updatePageCount(Object.keys(renderedIds).length);
          // Some items rendered fine, but this specific one didn't — surface
          // it instead of failing silently (previously the only sign was a
          // header count that didn't match how many cards were on screen).
          var stillUnresolved = toRescan.filter(function (id) { return !renderedIds[id]; });
          var note = byId("wishlistNote");
          if (note) {
            if (hadError && stillUnresolved.length) {
              note.textContent = stillUnresolved.length === 1
                ? "1 saved item couldn't be loaded right now — try reloading the page."
                : stillUnresolved.length + " saved items couldn't be loaded right now — try reloading the page.";
              note.hidden = false;
            } else {
              note.hidden = true;
            }
          }
        }
      });
    });
  }

  var resizeTimer = null;
  window.addEventListener("resize", function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () { layoutCardInfo(byId("wishlistGrid")); }, 150);
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
